import { Plus, Search } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Card from "../components/Card"
import KanbanBoard from "../components/KanbanBoard"
import AddProjectModal from "../components/AddProjectModal"
import type { Project, ProjectTask } from "../types"

type ProjectCreatePayload = {
  name: string
  clientId: string
  status: string
  budgetHours: number
  dueDate: string
}

const ProjectsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    const projectId = searchParams.get("project")
    if (projectId) {
      navigate(`/projects/${projectId}`, { replace: true })
    }
  }, [searchParams, navigate])

  const fetchProjects = async () => {
    try {
      const res = await fetch("/api/projects")
      const data = await res.json()
      if (data.projects) {
        setProjects(data.projects)
      }
    } catch (error) {
      console.error("Failed to fetch projects", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchProjects()
  }, [])

  const handleCreateProject = async (projectData: ProjectCreatePayload) => {
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(projectData),
      })
      if (res.ok) {
        setIsAddModalOpen(false)
        fetchProjects()
      }
    } catch (error) {
      console.error("Failed to create project", error)
    }
  }

  const handleProjectStatusChange = async (projectId: string, newStatus: Project["status"]) => {
    const prevProjects = projects

    // Optimistic update
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              status: newStatus,
              updatedAt: new Date().toISOString(),
            }
          : p,
      ),
    )

    try {
      const res = await fetch(`/api/projects/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      })
      if (!res.ok) throw new Error("Failed")
    } catch {
      setProjects(prevProjects)
    }
  }

  const handleTaskStatusChange = async (
    projectId: string,
    taskId: string,
    newStatus: ProjectTask["status"],
  ) => {
    const prevProjects = projects

    // Optimistic update
    setProjects((prev) =>
      prev.map((p) => {
        if (p.id !== projectId) return p

        const updatedSubtasks = p.subtasks.map((task) =>
          task.id === taskId
            ? {
                ...task,
                status: newStatus,
                updatedAt: new Date().toISOString(),
              }
            : task,
        )

        // Update task counts
        const tasksDone = updatedSubtasks.filter((t) => t.status === "done").length
        const tasksTotal = updatedSubtasks.length

        return {
          ...p,
          subtasks: updatedSubtasks,
          tasksDone,
          tasksTotal,
          updatedAt: new Date().toISOString(),
        }
      }),
    )

    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      })
      if (!res.ok) throw new Error("Failed")
    } catch {
      setProjects(prevProjects)
    }
  }

  const handleAddTask = async (projectId: string, title: string) => {
    const tempId = `task-${Date.now()}`
    const newTask = {
      id: tempId,
      title,
      description: "",
      status: "todo" as const,
      assignee: "",
      dueDate: undefined,
      estimatedHours: 0,
      actualHours: 0,
      priority: "medium" as const,
      tags: [] as string[],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    // Optimistic
    setProjects(prev => prev.map(p =>
      p.id === projectId
        ? { ...p, subtasks: [...p.subtasks, newTask], tasksTotal: p.tasksTotal + 1 }
        : p
    ))

    try {
      const res = await fetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, projectId, status: "todo", priority: "medium" }),
      })
      if (res.ok) {
        const data = await res.json()
        setProjects(prev => prev.map(p =>
          p.id === projectId
            ? { ...p, subtasks: p.subtasks.map(t => t.id === tempId ? { ...t, id: data.id } : t) }
            : p
        ))
      } else {
        throw new Error("Failed")
      }
    } catch {
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, subtasks: p.subtasks.filter((t) => t.id !== tempId), tasksTotal: Math.max(0, p.tasksTotal - 1) }
            : p,
        ),
      )
    }
  }

  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects
    const q = searchQuery.toLowerCase()
    return projects.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.client.toLowerCase().includes(q) ||
      p.assignees.some(a => a.toLowerCase().includes(q))
    )
  }, [projects, searchQuery])

  if (isLoading) {
    return <div className="p-8 text-center text-stone">Loading projects...</div>
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-lg font-semibold text-slate-900">Project Kanban</p>
            <p className="text-sm text-stone">
              Drag projects between columns. Expand cards to manage subtasks.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss/90"
          >
            <Plus className="h-4 w-4" />
            Create Project
          </button>
        </div>
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search projects, clients, assignees..."
            className="w-full rounded-full border border-line bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-moss"
          />
        </div>
      </Card>

      <KanbanBoard
        projects={filteredProjects}
        onProjectStatusChange={handleProjectStatusChange}
        onTaskStatusChange={handleTaskStatusChange}
        onAddTask={handleAddTask}
      />

      <AddProjectModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleCreateProject}
      />
    </div>
  )
}

export default ProjectsPage
